// ###### IMPORTS
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../connections/AuthContext';
import routes_web from '../../connections/routes_web';
import routes_api from '../../connections/routes_api';
import { getNotes } from './moduleServices/notepad_services';
import { useNotepadClusters } from './moduleServices/useNotepadClusters';
import useOfflineFirstData from 'services/useOfflineFirstData';

// imported components
import MainNotes from './tab_notes/main_notes';
import MainClusters from './tab_clusters/main_clusters';
import TabNotepadSettings from './tab_settings/main_settings';
import NavbarTop from './moduleComponents/NavbarTop';
import NavbarBottom from './moduleComponents/NavbarBottom';
import SearchBar from './moduleComponents/SearchBar';


// ###### MAIN COMPONENT
const NotepadMain = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('notes');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Use offline-first hook for notes
  const {
    data: notesData,
    isLoading: isNotesLoading,
    refresh: refreshNotes,
    updateOptimistically: updateNotesOptimistically
  } = useOfflineFirstData(routes_api.notepad_notes);

  // Sort notes by updated_at descending (most recent first)
  const notes = useMemo(() => {
    if (!notesData || notesData.length === 0) return [];
    return [...notesData].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return dateB - dateA; // Descending order
    });
  }, [notesData]);

  // Filter notes by search (title + content, case insensitive)
  const filteredNotes = useMemo(() => {
    if (!searchOpen || !searchQuery.trim()) return notes;
    const q = searchQuery.trim().toLowerCase();
    return notes.filter(
      (n) =>
        (n.title || '').toLowerCase().includes(q) ||
        (n.content || '').toLowerCase().includes(q)
    );
  }, [notes, searchOpen, searchQuery]);
  
  // Use hook for clusters management
  const { 
    clusters, 
    isLoading: isClustersLoading, 
    createCluster: handleClusterCreate,
    updateCluster: handleClusterUpdate,
    deleteCluster: handleClusterDelete
  } = useNotepadClusters();

  // ##### REFACTORED: Remove wrapper functions, pass hooks directly
  // Single source of truth: notes array is managed by useOfflineFirstData
  // Optimistic updates directly update notes array, no need for manual sync

  // ### HANDLERS
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'settings') setSearchOpen(false);
  };
  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };
  const handleSearchToggle = () => {
    if (searchOpen) handleSearchClose();
    else setSearchOpen(true);
  };

  // ### RETURN VIEW
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="max-w-5xl w-full mx-auto pt-14 pb-20">

        {/* Search bar (notes and clusters tabs) */}
        {(activeTab === 'notes' || activeTab === 'clusters') && searchOpen && (
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={handleSearchClose}
            count={filteredNotes.length}
          />
        )}

        {/* Tabs Content */}
        {activeTab === 'notes' && (
          <MainNotes 
            notes={searchOpen ? filteredNotes : notes}
            isNotesLoading={isNotesLoading}
            refreshNotes={refreshNotes}
            updateNotesOptimistically={updateNotesOptimistically}
            clusters={clusters}
          />
        )}
        {activeTab === 'clusters' && (
          <MainClusters 
            notes={searchOpen ? filteredNotes : notes}
            isNotesLoading={isNotesLoading}
            clusters={clusters}
            isClustersLoading={isClustersLoading}
            refreshNotes={refreshNotes}
            updateNotesOptimistically={updateNotesOptimistically}
          />
        )}
        {activeTab === 'settings' && (
          <TabNotepadSettings 
            clusters={clusters}
            isClustersLoading={isClustersLoading}
            onClusterCreate={handleClusterCreate}
            onClusterUpdate={handleClusterUpdate}
            onClusterDelete={handleClusterDelete}
          />
        )}

      </div>

      {/* Navbar Top */}
      <NavbarTop
        title="Notepad"
        onHomeClick={() => navigate(routes_web.accounts_home)}
        onLogoutClick={logout}
        handleTabChange={handleTabChange}
        activeTab={activeTab}
      />

      {/* Navbar Bottom */}
      <NavbarBottom
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        onSearchToggle={handleSearchToggle}
        isSearchOpen={searchOpen}
      />

    </div>
  );
};

export default NotepadMain;
